import * as React from 'react';

import { formatTime } from '@/lib/utils/date';
import type { Activity } from '../types';

interface ShowTimeProps {
  data: Activity | null;
}

export function ShowTime({ data }: ShowTimeProps) {
  const [displayTime, setDisplayTime] = React.useState(0);

  React.useEffect(() => {
    if (!data) {
      setDisplayTime(0);
      return;
    }

    // Check if activity is running (has lastStartTime but no lastEndTime)
    const isRunning = Boolean(data.lastStartTime && !data.lastEndTime);

    // If activity is stopped, show userEnteredTimeInSeconds
    if (!isRunning) {
      setDisplayTime(data.userEnteredTimeInSeconds || 0);
      return;
    }

    // Activity is running - calculate and auto-increment
    const base = Number(data.runTimeInSeconds || 0);
    const serverStart = new Date(data.lastStartTime!).getTime();
    const serverNow = data.currentTime
      ? new Date(data.currentTime).getTime()
      : Date.now();
    const elapsedOnServer = Math.max(0, Math.floor((serverNow - serverStart) / 1000));
    const initialRun = base + elapsedOnServer;
    const fetchPerf = performance.now();

    setDisplayTime(initialRun);

    const interval = setInterval(() => {
      const elapsedSinceFetch = Math.floor((performance.now() - fetchPerf) / 1000);
      setDisplayTime(() => initialRun + elapsedSinceFetch);
    }, 1000);

    return () => clearInterval(interval);
  }, [data]);

  return <span>{formatTime(displayTime)}</span>;
}

// Helper function to calculate current running time
export function calculateRunningTime(activityData: Activity | null): number {
  if (!activityData?.lastStartTime) return 0;
  const base = Number(activityData.runTimeInSeconds || 0);
  const serverStart = new Date(activityData.lastStartTime).getTime();
  const elapsed = Math.max(0, Math.floor((Date.now() - serverStart) / 1000));
  return base + elapsed;
}
